// Stale-chunk recovery.
//
// Each deploy emits JS chunks with fresh content-hash names and removes the old
// ones. A browser tab (or a CDN-cached index.html) from before the deploy can
// still ask for an old chunk; the host answers the missing file with index.html
// (the SPA fallback), so the browser refuses the "text/html" module script and a
// lazy route fails to load. The fix is to reload once to pick up the fresh
// index.html + chunk map; the reload is guarded so a genuinely broken deploy
// cannot trap the user in an endless reload loop.

const RELOAD_FLAG = 'kg:stale-chunk-reloaded-at';
const RELOAD_WINDOW_MS = 10_000;

// True when an error looks like a hashed chunk that no longer exists after a deploy.
export function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return (
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    /module script.*mime type of "text\/html"/i.test(message) ||
    /expected a javascript(?:-or-wasm)? module/i.test(message) ||
    /loading chunk \S+ failed/i.test(message) ||
    /chunkloaderror/i.test(message)
  );
}

// Reload once to fetch fresh assets. Returns false (without reloading) if a reload
// was already attempted in the last RELOAD_WINDOW_MS, so the caller can fall back
// to showing an error UI instead of looping.
export function reloadOnceForStaleChunks(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) ?? '0');
    if (Number.isFinite(last) && Date.now() - last < RELOAD_WINDOW_MS) {
      return false;
    }
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode / blocked) — reload anyway.
  }
  window.location.reload();
  return true;
}
